import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatButton } from "@angular/material/button";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { InitialInterviewPage } from "../../../enums";
import { PageService } from "../service/page-service";
import { NgIf, NgOptimizedImage } from "@angular/common";
import { InitialInterviewApiService } from "../initial-interview-api.service";
import { InitialInterviewQuestion } from "../../../../typings";
import { PreInterviewQuestionsService } from "../../../pre-interview-questions/pre-interview-questions.service";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { Router } from "@angular/router";
import { TalentHappyPathDataService } from "../../shared/service/talent-happy-path-data-service";
import { timeout } from "rxjs/operators";
import { combineQuestions, initialInterviewClickIllDoThisLater, routeGatherToProfileOrThankYou } from "../util";
import { TalentProfileApiService } from '../../talent-profile/talent-profile-api.service';
import { VideoInterviewFlowService } from "../video-interview/service/video-interview-flow.service";

@Component({
    selector: 'app-initial-interview-splash-page',
    imports: [
        MatButton,
        TranslateModule,
        NgIf,
        MatProgressSpinnerModule
    ],
    templateUrl: './initial-interview-splash-page.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './initial-interview-splash-page.component.scss'
})
export class InitialInterviewSplashPageComponent implements OnInit {
  timeout_duration = 180000;
  jobPostingId: number;
  jobTitle: string;
  orderId: number;
  talent: Talent;
  @Output() initialInterviewQuestionsEmitter: EventEmitter<InitialInterviewQuestion[]> = new EventEmitter<InitialInterviewQuestion[]>();

  showSpinner: boolean = true;
  noQuestions: boolean = false;
  isButtonDisabled: boolean = true;
  showLaterButton: boolean = true;
  contentText: string = String(this.translateService.instant('talent-happy-path.initial-interview-questions.splash-page.check-for-questions'));
  thankYouPath: string = '/thank-you';

  constructor(
    private translateService: TranslateService,
    private initialInterviewApi: InitialInterviewApiService,
    private pageService: PageService,
    private preInterviewQuestionsService: PreInterviewQuestionsService,
    private router: Router,
    private talentHappyPathDataService: TalentHappyPathDataService,
    private talentProfileApiService: TalentProfileApiService,
    private videoInterviewFlowService: VideoInterviewFlowService
  ) {}

  ngOnInit() {
    this.talent = this.talentHappyPathDataService.talent;
    this.jobPostingId = this.talentHappyPathDataService.postingId;
    this.jobTitle = this.talentHappyPathDataService.postingInfo.jobTitle;
    this.orderId = this.talentHappyPathDataService.postingInfo.orderId;
    this.checkForQuestions();
  }

  checkForQuestions(): void {
    const preInterviewQuestionPromise = this.preInterviewQuestionsService
      .getQuestionsAndAnswersForPostingId(this.jobPostingId, this.talent.personId)
      .pipe(timeout(this.timeout_duration))
      .toPromise()
      .catch(error => {
        console.error('Error fetching pre-interview questions:', error);
        return [];
      });

    const customQuestionsPromise = this.initialInterviewApi
      .provideCustomQuestions(this.jobPostingId, this.talent.personId)
      .pipe(timeout(this.timeout_duration))
      .toPromise()
      .catch(error => {
        console.error('Error fetching custom questions:', error);
        return [];
      });
    const delayPromise = new Promise(resolve => setTimeout(resolve, 3000));

    Promise.all([customQuestionsPromise, preInterviewQuestionPromise, delayPromise])
      .then(([customQuestions, preInterviewQuestions]) => {

        const initialInterviewQuestions: InitialInterviewQuestion[] = combineQuestions(preInterviewQuestions, customQuestions);
        this.noQuestions = initialInterviewQuestions.length == 0;
        this.initialInterviewQuestionsEmitter.emit(initialInterviewQuestions);
        this.updatePageState();
      });
  }

  updatePageState(): void {
    this.showSpinner = false;
    this.isButtonDisabled = false;
    if (!this.noQuestions) {
      this.pageService.setActivePage(InitialInterviewPage.QUESTIONS_PAGE);
    } else {
      this.contentText = String(this.translateService.instant('talent-happy-path.initial-interview-questions.splash-page.content-text-when-no-questions'));
      this.showLaterButton = false;
    }
  }

  clickIllDoThisLater() {
    if (this.videoInterviewFlowService.shouldShowForWorkflowA()) {
      // Workflow A: show the video interview before leaving, even when skipping the questions.
      this.videoInterviewFlowService.enterVideoInterview();
      return;
    }
    if (this.talentHappyPathDataService.gather) {
      initialInterviewClickIllDoThisLater('', this.talent, this.jobPostingId, this.orderId, this.jobTitle, this.initialInterviewApi, this.talentHappyPathDataService, this.router);
      routeGatherToProfileOrThankYou(this.talentHappyPathDataService.gather.talentPersonId, this.talentProfileApiService, this.router);
    } else {
      initialInterviewClickIllDoThisLater(this.thankYouPath, this.talent, this.jobPostingId, this.orderId, this.jobTitle, this.initialInterviewApi, this.talentHappyPathDataService, this.router);
    }
  }

  navigateToTalentProfile() {
    if (this.videoInterviewFlowService.shouldShowForWorkflowA()) {
      this.videoInterviewFlowService.enterVideoInterview();
      return;
    }
    this.videoInterviewFlowService.goToTalentProfile();
  }
}
